import { useState, useCallback, useEffect } from 'react';

const DEFAULT_FILTERS = {
  confidenceRange: [0, 100],
  marginRange: [-20, 20],
  competitivenessRange: [0, 100],
  openSeatsOnly: false,
  activePreset: null
};

function parseRange(str, defaultVal) {
  if (!str) return defaultVal;
  const [min, max] = str.split(',').map(Number);
  return [isNaN(min) ? defaultVal[0] : min, isNaN(max) ? defaultVal[1] : max];
}

function loadFiltersFromStorage() {
  try {
    const stored = localStorage.getItem('house-races-filters');
    if (stored) {
      return { ...DEFAULT_FILTERS, ...JSON.parse(stored) };
    }
  } catch (err) {
    console.warn('Failed to load filters from localStorage:', err);
  }
  return DEFAULT_FILTERS;
}

/**
 * Hook for managing filter state with localStorage persistence
 *
 * Note: This uses localStorage instead of URL for now since react-router is not set up
 * Can be upgraded to URL params when routing is added
 *
 * @returns {Object} { filters, updateFilters, resetFilters }
 */
export function useFilterState() {
  // Use useState to ensure re-renders when filters change
  const [filters, setFilters] = useState(loadFiltersFromStorage);

  // Listen for storage changes from other components
  useEffect(() => {
    const handleStorageChange = () => {
      setFilters(loadFiltersFromStorage());
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const updateFilters = useCallback((updates) => {
    try {
      const currentFilters = loadFiltersFromStorage();
      const newFilters = { ...currentFilters, ...updates };
      localStorage.setItem('house-races-filters', JSON.stringify(newFilters));

      // Update local state immediately
      setFilters(newFilters);

      // Trigger a storage event to notify other components
      window.dispatchEvent(new Event('storage'));
    } catch (err) {
      console.warn('Failed to save filters to localStorage:', err);
    }
  }, []);

  const resetFilters = useCallback(() => {
    try {
      localStorage.removeItem('house-races-filters');

      // Update local state immediately
      setFilters(DEFAULT_FILTERS);

      // Trigger a storage event to notify other components
      window.dispatchEvent(new Event('storage'));
    } catch (err) {
      console.warn('Failed to reset filters:', err);
    }
  }, []);

  return { filters, updateFilters, resetFilters };
}
